import React, { useState, useCallback, useRef } from 'react';
import { PageWrapper, PageHeader } from '../components/partials';
import Reports from '../services/reports';
import { expandMetadata, expandRow } from '../utils/reports';
import css from './page.css';
import Filters from './components/filters/Filters';
import VisualizationTabs from './components/visualizationTabs/VisualizationTabs';
import { buildQueryParams, buildQueryKey, transformToSunburstData } from './queryUtils';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS';
const reportsService = new Reports();

const expandRows = reportData => {
    const metadata = expandMetadata(reportData.metadata, true);
    return (reportData.results || []).map(row => expandRow(metadata, row));
};

const AnomalyAnalysisPage = () => {
    const [sunburstData, setSunburstData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasQueried, setHasQueried] = useState(false);

    const cachedResultsRef = useRef(null);
    const latestRunRef = useRef(0);

    const handleRun = useCallback(async state => {
        const queryKey = buildQueryKey(state);
        const noNewFetchNeeded = cachedResultsRef.current?.queryKey === queryKey;

        if (noNewFetchNeeded) {
            const cachedDimensionOrder = cachedResultsRef.current.dimensionResults.map(r => r.dimension.value);
            const newDimensionOrder = state.dimensions.map(d => d.value);
            const orderChanged = cachedDimensionOrder.some((v, i) => v !== newDimensionOrder[i]);

            if (orderChanged) {
                const reordered = state.dimensions.map(d =>
                    cachedResultsRef.current.dimensionResults.find(r => r.dimension.value === d.value)
                );
                if (reordered.some(r => !r)) {
                    // Cache is missing a dimension (e.g. removed then re-added) — fall through to fetch
                } else {
                    cachedResultsRef.current = { ...cachedResultsRef.current, dimensionResults: reordered };
                    setSunburstData(transformToSunburstData(cachedResultsRef.current.dimensionResults, state));
                    return;
                }
            } else {
                setSunburstData(transformToSunburstData(cachedResultsRef.current.dimensionResults, state));
                return;
            }
        }

        const runId = ++latestRunRef.current;
        setIsLoading(true);
        setError(false);
        try {
            const dimensionResults = await Promise.all(
                state.dimensions.map(async dimension => {
                    const params = buildQueryParams([dimension], state);
                    const reportData = await reportsService.runQuery(params, { suffix: dimension.value });
                    return { dimension, rows: expandRows(reportData) };
                })
            );
            if (runId !== latestRunRef.current) return;

            cachedResultsRef.current = { queryKey, dimensionResults, lastState: state };
            setSunburstData(transformToSunburstData(dimensionResults, state));
            setHasQueried(true);
        } catch (err) {
            if (runId !== latestRunRef.current) return;

            cachedResultsRef.current = null;
            setError(true);
        } finally {
            if (runId === latestRunRef.current) setIsLoading(false);
        }
    }, []);

    return (
        <PageWrapper className={css.pageWrapper} disablePadding>
            <div className={css.header}>
                <PageHeader text={`${TRANSLATION_PREFIX}.PAGE_TITLE`} shouldAddPadding />
            </div>
            <div className={css.body}>
                <Filters onRun={handleRun} />
                <div className={css.content}>
                    <VisualizationTabs data={sunburstData} isLoading={isLoading} error={error} hasQueried={hasQueried} />
                </div>
            </div>
        </PageWrapper>
    );
};

export default AnomalyAnalysisPage;
